import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import en from './en.json';
import sv from './sv.json';

export type Locale = 'en' | 'sv';
export type MessageKey = keyof typeof en;

const DICTS: Record<Locale, Record<string, string>> = { en, sv };
const STORAGE_KEY = 'singlings.locale';

function pickInitial(): Locale {
  if (typeof window === 'undefined') return 'en';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'en' || stored === 'sv') return stored;
  const nav = (navigator.language || 'en').toLowerCase();
  if (nav.startsWith('sv')) return 'sv';
  return 'en';
}

interface I18nCtx {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: MessageKey) => string;
}

const Ctx = createContext<I18nCtx | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => pickInitial());

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      window.localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* localStorage may be unavailable */
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const t = useCallback(
    (key: MessageKey): string => {
      const dict = DICTS[locale];
      return dict[key] ?? DICTS.en[key] ?? key;
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return createElement(Ctx.Provider, { value }, children);
}

export function useT(): I18nCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useT must be used inside <I18nProvider>');
  return v;
}
